import React, { useState, useEffect } from 'react';
import { post, get, del, patch } from 'aws-amplify/api';
import { useAuth } from '../contexts/AuthContext';
import './Menu.css';

// Dietary tags with full names
const DIETARY_TAGS = [
  { id: 'V', label: 'Vegetarian', color: '#22c55e' },
  { id: 'GF', label: 'Gluten-Free', color: '#3b82f6' },
  { id: 'VG', label: 'Vegan', color: '#a855f7' },
  { id: 'DF', label: 'Dairy-Free', color: '#f97316' },
];

// Format price as currency string
const formatPrice = (price) => {
  const numericValue = parseFloat(String(price).replace(/[^0-9.]/g, ''));
  if (isNaN(numericValue)) return '$0.00';
  return `$${numericValue.toFixed(2)}`;
};

const Menu = () => {
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  // Item form fields
  const [itemName, setItemName] = useState('');
  const [itemPrice, setItemPrice] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [itemDietary, setItemDietary] = useState([]);
  const [itemImage, setItemImage] = useState(null);
  const [itemImagePreview, setItemImagePreview] = useState(null);
  const [itemFeatured, setItemFeatured] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const { getAuthToken } = useAuth();

  // Fetch menu on component mount
  useEffect(() => {
    fetchMenu();
  }, []);

  const fetchMenu = async () => {
    try {
      setLoadingMenu(true);
      setError(null);

      const restOperation = get({
        apiName: 'ChulasAdminAPI',
        path: '/v1/menu',
      });

      const response = await restOperation.response;
      const data = await response.body.json();

      // Filter out the Featured category from admin view (it's auto-generated)
      const regularCategories = (data.categories || []).filter(cat => cat.category_id !== 'featured');
      setCategories(regularCategories);
      if (regularCategories.length > 0) {
        setActiveCategory(regularCategories[0]);
      }
    } catch (err) {
      console.error('Error fetching menu:', err);
      setError('Failed to fetch menu');
    } finally {
      setLoadingMenu(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setItemImage(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setItemImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadItemImage = async (itemId, token) => {
    if (!itemImage) return null;

    try {
      const reader = new FileReader();

      return new Promise((resolve, reject) => {
        reader.onloadend = async () => {
          try {
            const base64Image = reader.result;

            const restOperation = post({
              apiName: 'ChulasAdminAPI',
              path: `/v1/admin/menu/items/${itemId}/upload-image`,
              options: {
                headers: {
                  Authorization: token,
                },
                body: {
                  image_data: base64Image,
                  content_type: itemImage.type,
                },
              },
            });

            const response = await restOperation.response;
            const data = await response.body.json();
            resolve(data.image_url);
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(itemImage);
      });
    } catch (err) {
      console.error('Error uploading image:', err);
      throw new Error('Failed to upload image');
    }
  };

  const createCategory = async (e) => {
    e.preventDefault();

    if (!categoryName.trim()) {
      setError('Please enter a category name');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = post({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/menu/categories',
        options: {
          headers: {
            Authorization: token,
          },
          body: {
            name: categoryName.trim(),
          },
        },
      });

      await restOperation.response;

      setSuccessMessage('Category created successfully!');
      setCategoryName('');
      setShowCategoryForm(false);
      await fetchMenu();
    } catch (err) {
      console.error('Error creating category:', err);
      setError(err.message || 'Failed to create category. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateCategory = async (e) => {
    e.preventDefault();

    if (!editingCategory || !categoryName.trim()) {
      setError('Please enter a category name');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = patch({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/menu/categories/${editingCategory.category_id}`,
        options: {
          headers: {
            Authorization: token,
          },
          body: {
            name: categoryName.trim(),
          },
        },
      });

      await restOperation.response;

      setSuccessMessage('Category updated successfully!');
      setCategoryName('');
      setEditingCategory(null);
      setShowCategoryForm(false);
      await fetchMenu();
    } catch (err) {
      console.error('Error updating category:', err);
      setError(err.message || 'Failed to update category. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const deleteCategory = async (category) => {
    if (!window.confirm(`Are you sure you want to delete "${category.name}"? This will also delete all items in this category.`)) {
      return;
    }

    try {
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = del({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/menu/categories/${category.category_id}`,
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      await restOperation.response;

      setSuccessMessage('Category deleted successfully!');
      await fetchMenu();
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err.message || 'Failed to delete category. Please try again.');
    }
  };

  const reorderCategories = async (newCategories) => {
    try {
      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const category_orders = newCategories.map((cat, index) => ({
        category_id: cat.category_id,
        display_order: index + 1
      }));

      const restOperation = post({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/menu/categories/reorder',
        options: {
          headers: {
            Authorization: token,
          },
          body: {
            category_orders
          },
        },
      });

      await restOperation.response;
      setCategories(newCategories);
    } catch (err) {
      console.error('Error reordering categories:', err);
      setError('Failed to reorder categories');
    }
  };

  const moveCategory = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= categories.length) return;

    const newCategories = [...categories];
    [newCategories[index], newCategories[newIndex]] = [newCategories[newIndex], newCategories[index]];
    reorderCategories(newCategories);
  };

  const createMenuItem = async (e) => {
    e.preventDefault();

    if (!activeCategory || !itemName.trim() || !itemPrice.trim() || !itemDescription.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = post({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/menu/items',
        options: {
          headers: {
            Authorization: token,
          },
          body: {
            category_id: activeCategory.category_id,
            name: itemName.trim(),
            price: itemPrice.trim(),
            description: itemDescription.trim(),
            dietary: itemDietary,
            featured: itemFeatured,
          },
        },
      });

      const response = await restOperation.response;
      const data = await response.body.json();
      const itemId = data.item.item_id;

      // Upload image if selected
      if (itemImage) {
        await uploadItemImage(itemId, token);
      }

      setSuccessMessage('Menu item created successfully!');
      resetItemForm();
      await fetchMenu();
    } catch (err) {
      console.error('Error creating menu item:', err);
      setError(err.message || 'Failed to create menu item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateMenuItem = async (e) => {
    e.preventDefault();

    if (!editingItem || !itemName.trim() || !itemPrice.trim() || !itemDescription.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = patch({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/menu/items/${editingItem.item_id}`,
        options: {
          headers: {
            Authorization: token,
          },
          body: {
            name: itemName.trim(),
            price: itemPrice.trim(),
            description: itemDescription.trim(),
            dietary: itemDietary,
            featured: itemFeatured,
          },
        },
      });

      await restOperation.response;

      // Upload image if selected
      if (itemImage) {
        await uploadItemImage(editingItem.item_id, token);
      }

      setSuccessMessage('Menu item updated successfully!');
      resetItemForm();
      await fetchMenu();
    } catch (err) {
      console.error('Error updating menu item:', err);
      setError(err.message || 'Failed to update menu item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const deleteMenuItem = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
      return;
    }

    try {
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      const restOperation = del({
        apiName: 'ChulasAdminAPI',
        path: `/v1/admin/menu/items/${item.item_id}`,
        options: {
          headers: {
            Authorization: token,
          },
        },
      });

      await restOperation.response;

      setSuccessMessage('Menu item deleted successfully!');
      await fetchMenu();
    } catch (err) {
      console.error('Error deleting menu item:', err);
      setError(err.message || 'Failed to delete menu item. Please try again.');
    }
  };

  const startEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryName(category.name);
    setShowCategoryForm(true);
  };

  const startEditItem = (item) => {
    setEditingItem(item);
    setItemName(item.name);
    // Parse price to ensure it's just the number (remove $ if present)
    const priceValue = parseFloat(String(item.price).replace(/[^0-9.]/g, ''));
    setItemPrice(!isNaN(priceValue) ? priceValue.toFixed(2) : '');
    setItemDescription(item.description);
    setItemDietary(item.dietary || []);
    setItemImagePreview(item.image_url || null);
    setItemImage(null);
    setItemFeatured(item.featured || false);
    setShowItemForm(true);
  };

  const resetItemForm = () => {
    setItemName('');
    setItemPrice('');
    setItemDescription('');
    setItemDietary([]);
    setItemImage(null);
    setItemImagePreview(null);
    setItemFeatured(false);
    setEditingItem(null);
    setShowItemForm(false);
  };

  const toggleDietaryTag = (tagId) => {
    setItemDietary(prev =>
      prev.includes(tagId)
        ? prev.filter(t => t !== tagId)
        : [...prev, tagId]
    );
  };

  return (
    <div className="menu-container">
      <h1>Menu Manager</h1>
      <p className="page-description">
        Manage menu categories and items. The order of categories here determines how they appear in the mobile app.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}

      <div className="menu-layout">
        {/* Sidebar - Categories */}
        <div className="menu-sidebar">
          <div className="sidebar-header">
            <h2>Categories</h2>
            <button
              onClick={() => {
                setEditingCategory(null);
                setCategoryName('');
                setShowCategoryForm(true);
              }}
              className="btn btn-primary btn-sm"
            >
              + Add Category
            </button>
          </div>

          {loadingMenu ? (
            <div className="loading-spinner">Loading...</div>
          ) : (
            <div className="categories-list">
              {categories.map((category, index) => (
                <div
                  key={category.category_id}
                  className={`category-item ${activeCategory?.category_id === category.category_id ? 'active' : ''}`}
                >
                  <div
                    className="category-content"
                    onClick={() => setActiveCategory(category)}
                  >
                    <span className="category-name">{category.name}</span>
                    <span className="category-count">({category.items?.length || 0})</span>
                  </div>
                  <div className="category-actions">
                    <button
                      onClick={() => moveCategory(index, 'up')}
                      disabled={index === 0}
                      className="btn-icon"
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveCategory(index, 'down')}
                      disabled={index === categories.length - 1}
                      className="btn-icon"
                      title="Move down"
                    >
                      ↓
                    </button>
                    <button
                      onClick={() => startEditCategory(category)}
                      className="btn-icon"
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => deleteCategory(category)}
                      className="btn-icon btn-delete"
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Main Content - Items */}
        <div className="menu-main">
          {showCategoryForm ? (
            <div className="form-section">
              <h2>{editingCategory ? 'Edit Category' : 'Create New Category'}</h2>
              <form onSubmit={editingCategory ? updateCategory : createCategory}>
                <div className="form-group">
                  <label htmlFor="categoryName">Category Name *</label>
                  <input
                    type="text"
                    id="categoryName"
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                    placeholder="e.g., Appetizers, Tacos, Drinks"
                    disabled={loading}
                    maxLength={50}
                  />
                </div>
                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !categoryName.trim()}
                  >
                    {loading ? 'Saving...' : editingCategory ? 'Update Category' : 'Create Category'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowCategoryForm(false);
                      setEditingCategory(null);
                      setCategoryName('');
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          ) : showItemForm ? (
            <div className="form-section">
              <h2>{editingItem ? 'Edit Menu Item' : 'Create New Menu Item'}</h2>
              <form onSubmit={editingItem ? updateMenuItem : createMenuItem}>
                <div className="form-group">
                  <label htmlFor="itemName">Item Name *</label>
                  <input
                    type="text"
                    id="itemName"
                    value={itemName}
                    onChange={(e) => setItemName(e.target.value)}
                    placeholder="e.g., Carne Asada Tacos"
                    disabled={loading}
                    maxLength={100}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="itemPrice">Price *</label>
                  <div className="price-input-wrapper">
                    <span className="price-prefix">$</span>
                    <input
                      type="number"
                      id="itemPrice"
                      value={itemPrice}
                      onChange={(e) => setItemPrice(e.target.value)}
                      onBlur={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value)) {
                          setItemPrice(value.toFixed(2));
                        }
                      }}
                      placeholder="0.00"
                      disabled={loading}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="itemDescription">Description *</label>
                  <textarea
                    id="itemDescription"
                    value={itemDescription}
                    onChange={(e) => setItemDescription(e.target.value)}
                    placeholder="Describe the dish..."
                    rows="4"
                    disabled={loading}
                    maxLength={300}
                  />
                </div>

                <div className="form-group">
                  <label>Dietary Tags</label>
                  <div className="dietary-tags">
                    {DIETARY_TAGS.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        className={`dietary-tag ${itemDietary.includes(tag.id) ? 'selected' : ''}`}
                        style={{
                          backgroundColor: itemDietary.includes(tag.id) ? tag.color : '#e5e5e5',
                          color: itemDietary.includes(tag.id) ? 'white' : '#666'
                        }}
                        onClick={() => toggleDietaryTag(tag.id)}
                        disabled={loading}
                      >
                        {tag.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={itemFeatured}
                      onChange={(e) => setItemFeatured(e.target.checked)}
                      disabled={loading}
                    />
                    <span>Feature this item (show in Featured tab)</span>
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="itemImage">Image</label>
                  <input
                    type="file"
                    id="itemImage"
                    accept="image/*"
                    onChange={handleImageChange}
                    disabled={loading}
                  />
                  <small>Max file size: 5MB. Supported formats: JPG, PNG, GIF</small>
                  {itemImagePreview && (
                    <div className="image-preview">
                      <img src={itemImagePreview} alt="Preview" />
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !itemName.trim() || !itemPrice.trim() || !itemDescription.trim()}
                  >
                    {loading ? 'Saving...' : editingItem ? 'Update Item' : 'Create Item'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetItemForm}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          ) : activeCategory ? (
            <div className="items-section">
              <div className="section-header">
                <h2>{activeCategory.name}</h2>
                <button
                  onClick={() => setShowItemForm(true)}
                  className="btn btn-primary"
                >
                  + Add Item
                </button>
              </div>

              {activeCategory.items?.length === 0 ? (
                <div className="empty-state">
                  <p>No items in this category</p>
                  <small>Click "Add Item" to create your first menu item</small>
                </div>
              ) : (
                <div className="items-grid">
                  {activeCategory.items?.map((item) => (
                    <div key={item.item_id} className="item-card">
                      {item.image_url && (
                        <div className="item-image">
                          <img src={item.image_url} alt={item.name} />
                        </div>
                      )}
                      <div className="item-content">
                        <div className="item-header">
                          <h3>
                            {item.name}
                            {item.featured && <span className="featured-badge">Featured</span>}
                          </h3>
                          <span className="item-price">{formatPrice(item.price)}</span>
                        </div>
                        <p className="item-description">{item.description}</p>
                        {item.dietary && item.dietary.length > 0 && (
                          <div className="item-dietary">
                            {item.dietary.map((tagId) => {
                              const tag = DIETARY_TAGS.find(t => t.id === tagId);
                              return tag ? (
                                <span
                                  key={tagId}
                                  className="dietary-badge"
                                  style={{ backgroundColor: tag.color }}
                                >
                                  {tag.label}
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>
                      <div className="item-actions">
                        <button
                          onClick={() => startEditItem(item)}
                          className="btn-edit"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteMenuItem(item)}
                          className="btn-delete"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="empty-state">
              <p>Select a category to view its items</p>
              <small>Or create a new category to get started</small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Menu;
